import { faCrosshairs } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { notification } from "antd";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import * as React from "react";
import Map, { GeolocateControl, Layer, MapRef, NavigationControl, Popup, Source } from "react-map-gl/mapbox";
import { globalAppConfig } from "../../config";
import GlobalStoreContext from "../../context/GlobalStoreContext";
import Helper from "../../Helper";
import { Area } from "../../models/Area";
import { GateToDropoffRoute } from "../../models/GateToDropoffRoute";
import { HaulRoute } from "../../models/HaulRoute";
import { MapLayer } from "../../models/MapLayer";
import { MapObject } from "../../models/MapObject";
import MapErrorBoundary from "../Map/MapErrorBoundary";
import MapPopup from "../Map/MapPopup";
import WaypointStepMarker from "../Map/WaypointStepMarker";

type DamnMapProps = {
    mapObjects: MapObject[];
    mapId: string;
    route?: Area | HaulRoute | GateToDropoffRoute | null;
    // Routes whose step markers show alongside the main route's, e.g. the leg from the gate to the dropoff.
    stepRoutes?: GateToDropoffRoute[];
    setUserLocation: (coords: GeolocationCoordinates | null) => void;
    geoControlRef?: React.RefObject<mapboxgl.GeolocateControl> | null;
    currentStep?: number;
    mapStyleOverride?: string;
    project?: any;
    disableZoom?: boolean;
    onMapObjectClick?: (mapObject: MapObject) => void;
};

const DEFAULT_CENTER = { longitude: -97.314835, latitude: 37.697948 }; // Wichita, fallback when no project or map objects have coordinates
const DEFAULT_MAX_ZOOM = 17; // Used for fitting the map bounds to the objects
const SOURCE_ID = "damnMap";
const GEOMETRY_DRAW_ORDER: Record<MapObject["geometry"], number> = { polygon: 0, lineString: 1, point: 2 };

const DamnMap: React.FC<DamnMapProps> = ({
    mapObjects,
    mapId,
    route,
    stepRoutes = [],
    setUserLocation,
    geoControlRef,
    currentStep,
    mapStyleOverride,
    project,
    disableZoom = false,
    onMapObjectClick,
}) => {
    const context = React.useContext(GlobalStoreContext);
    const [popup, setPopup] = React.useState<MapObject | null>(null);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [mapBounds, setMapBounds] = React.useState<mapboxgl.LngLatBounds>(new mapboxgl.LngLatBounds());
    const [showRecenterButton, setShowRecenterButton] = React.useState<boolean>(false);
    const [metersPerPixel, setMetersPerPixel] = React.useState<number>(1);

    const mapRef = React.useRef<MapRef>(null);

    const userLocation = React.useRef<GeolocationCoordinates | null>(null);

    const mapGeoJson = React.useMemo(() => {
        return Helper.generateMapGeoJson(mapObjects);
    }, [mapObjects]);

    const onMapLoad = () => {
        const map = mapRef.current?.getMap();
        if (!map) return;
        Helper.addMarkerImages(map).then(() => {
            setLoading(false);
        });
        map.resize();
        updateMetersPerPixel();
    };

    const updateMetersPerPixel = () => {
        const map = mapRef.current?.getMap();
        if (!map) return;
        setMetersPerPixel(Helper.getMapMaxMetersPerPixelAtZoom(map));
    };

    const loadMapLayer = (object: MapObject) => {
        const map = mapRef?.current?.getMap();
        if (!map) {
            return null;
        }

        const iconSize = 0.25;

        switch (object.geometry) {
            case "point":
                return Helper.addIcon(object, SOURCE_ID, map, "visible", iconSize);
            case "lineString":
                // Sized like the logistics map, so a route's icons and end caps look the same on both: to the icon's
                // real-world width if it has one, otherwise scaled with zoom.
                return Helper.addLine(
                    object,
                    SOURCE_ID,
                    "visible",
                    object.iconWidth
                        ? Helper.getIconRealWorldSize(object, metersPerPixel)
                        : Helper.defaultIconSizeArray(true)
                );
            case "polygon":
                return Helper.addPolygon(object, SOURCE_ID, "visible", iconSize);
            default:
                return null;
        }
    };

    // Every layer shares the "top" slot, where mapbox draws later-added layers over earlier ones, and react-map-gl
    // never moves a layer after adding it. So objects that arrive in a later render (e.g. a route loading after its
    // gate) would land on top regardless of type - restack after each change instead of relying on render order.
    React.useEffect(() => {
        const map = mapRef.current?.getMap();
        if (loading || !map) {
            return;
        }

        const geometryByUuid = Object.fromEntries(mapObjects.map((object) => [object.uuid, object.geometry]));
        // Helper layers are named `<uuid>_<suffix>` (outlines, line caps, radiuses), so they move with their object.
        const drawOrder = (layerId: string) => GEOMETRY_DRAW_ORDER[geometryByUuid[layerId.split("_")[0]]] ?? 0;

        (map.getStyle()?.layers ?? [])
            .filter((layer) => "source" in layer && layer.source === SOURCE_ID)
            .map((layer) => layer.id)
            .sort((a, b) => drawOrder(a) - drawOrder(b))
            .forEach((layerId) => map.moveLayer(layerId));
    }, [loading, mapObjects, mapStyleOverride]);

    React.useEffect(() => {
        if (!loading) {
            fitMapBounds();
        }
    }, [loading]);

    React.useEffect(() => {
        // Used to solve issue of DOM container resizing to steps having/not having images and map not filling container.
        if (currentStep != null) {
            mapRef.current?.resize();
        }
    }, [currentStep]);

    const onClick = (event: any) => {
        const features = event.features;
        if (loading || !features || features.length === 0) {
            setPopup(null);
            return;
        }

        const clickedFeature = features[0];
        const clickedMapObject = mapObjects.find((obj) => obj.uuid === clickedFeature.layer.id);

        if (clickedMapObject) {
            setPopup(clickedMapObject);
            onMapObjectClick?.(clickedMapObject);
        }
    };

    const onHover = React.useCallback(
        (event: any) => {
            const map = mapRef.current?.getMap();
            if (!map || loading) {
                return;
            }

            const features = event.features;
            const mapObject = features?.length
                ? mapObjects.find((object) => object.uuid === features[0]?.properties?.uuid)
                : undefined;

            if (!mapObject && popup) {
                setPopup(null);
                map.getCanvas().style.cursor = "default";
                return;
            }
            if (context.editCurrentMappable || popup?.uuid === mapObject?.uuid) {
                return;
            }
            if (mapObject) {
                map.getCanvas().style.cursor = "pointer";
                setPopup(mapObject);
            }
        },
        [mapObjects, context.editCurrentMappable, loading, popup]
    );

    const fitMapBounds = () => {
        const map = mapRef.current?.getMap();
        if (!map) {
            return;
        }
        if (mapBounds.isEmpty()) {
            return;
        }
        map.fitBounds(mapBounds, {
            padding: 50,
            linear: true,
            maxZoom: DEFAULT_MAX_ZOOM,
        });
        map.resize();
    };

    React.useEffect(() => {
        if (!mapObjects || mapObjects.length === 0) {
            return;
        }

        let tempBounds = new mapboxgl.LngLatBounds();
        mapObjects?.map((object) => {
            if (object.coordinates) {
                tempBounds = Helper.addToBounds(object, tempBounds);
            }
        });
        setMapBounds(tempBounds);
    }, [mapObjects]);

    const initialViewState = () => {
        const projectLon = project?.lon_override || project?.lon;
        const projectLat = project?.lat_override || project?.lat;
        const firstObjectCenter = mapObjects[0]?.coordinates ? (mapObjects[0].getCenter() as [number, number]) : null;

        return {
            longitude: firstObjectCenter?.[0] ?? projectLon ?? DEFAULT_CENTER.longitude,
            latitude: firstObjectCenter?.[1] ?? projectLat ?? DEFAULT_CENTER.latitude,
            zoom: DEFAULT_MAX_ZOOM,
        };
    };
    const onDrag = () => {
        setShowRecenterButton(true);
    };

    const renderGeotiffLayer = () => {
        if (!project?.geotiff || project.geotiff.status !== "complete") {
            return null;
        }

        const geotiffId = `${project.geotiff.id}_geotiff`;
        return (
            <Source type="raster" id={geotiffId} url={`mapbox://nickdreiling.${project.geotiff.geotiff_name}`}>
                <Layer id={geotiffId} slot="middle" type="raster" source={geotiffId} />
            </Source>
        );
    };

    const renderProjectMapLayers = () => {
        const enabledLayers: MapLayer[] = project?.map_layers?.filter((layer: MapLayer) => layer.enabled) || [];
        return enabledLayers.map((layer) => (
            <Source key={layer.id} type="raster" id={`layer_${layer.id}`} tiles={[layer.url]}>
                <Layer id={`layer_${layer.id}`} slot="middle" type="raster" source={`layer_${layer.id}`} />
            </Source>
        ));
    };

    const renderRecenterButton = () => {
        if (!showRecenterButton) {
            return null;
        }

        const recenter = () => {
            if (userLocation.current) {
                mapRef.current?.flyTo({
                    center: [userLocation.current.longitude, userLocation.current.latitude],
                });
            } else {
                fitMapBounds();
            }
            setShowRecenterButton(false);
        };

        return (
            <button
                aria-label="Recenter Map Button"
                onClick={recenter}
                className="absolute bottom-8 right-2 z-50 bg-white rounded-lg p-2 px-3 shadow-md flex flex-row items-center gap-1 text-blue-500 text-sm cursor-pointer"
            >
                <FontAwesomeIcon icon={faCrosshairs} />
                Re-center
            </button>
        );
    };

    return (
        <MapErrorBoundary>
            <Map
                id={mapId}
                ref={mapRef}
                initialViewState={initialViewState()}
                onLoad={onMapLoad}
                mapStyle={mapStyleOverride || "mapbox://styles/mapbox/streets-v12"}
                mapboxAccessToken={globalAppConfig.MAP_BOX_ACCESS_TOKEN}
                interactiveLayerIds={mapObjects?.map((object) => object?.uuid || "")}
                onDragEnd={onDrag}
                onZoomEnd={updateMetersPerPixel}
                onClick={onClick}
                onMouseMove={onHover}
                scrollZoom={!disableZoom}
            >
                {popup && (
                    <Popup
                        longitude={popup?.getCenter()?.[0] || popup?.coordinates[0]}
                        latitude={popup?.getCenter()?.[1] || popup?.coordinates[1]}
                        closeButton={false}
                        closeOnClick={false}
                        onClose={() => setPopup(null)}
                        anchor="top"
                        maxWidth="none"
                    >
                        <MapPopup mapObject={popup} mappable={popup.getMappable(context)} />
                    </Popup>
                )}
                {renderGeotiffLayer()}
                {renderProjectMapLayers()}
                {!loading && mapGeoJson && (
                    <Source id={SOURCE_ID} type="geojson" data={mapGeoJson}>
                        {mapObjects.map((object) => loadMapLayer(object))}
                    </Source>
                )}
                {route &&
                    route.waypointDirection?.waypointSteps?.map((step) => (
                        <WaypointStepMarker
                            key={step.id}
                            animate={context.currentWaypointStep?.id === step.id}
                            waypointStep={step}
                        />
                    ))}
                {stepRoutes.flatMap(
                    (stepRoute) =>
                        stepRoute.waypointDirection?.waypointSteps
                            ?.filter((step) => !step._destroy && !step.hidden)
                            .map((step) => (
                                <WaypointStepMarker
                                    key={step.id}
                                    animate={context.currentWaypointStep?.id === step.id}
                                    waypointStep={step}
                                />
                            )) ?? []
                )}

                <GeolocateControl
                    ref={geoControlRef}
                    trackUserLocation={true}
                    showUserHeading={true}
                    positionOptions={{
                        enableHighAccuracy: true,
                    }}
                    onError={(e) => {
                        notification.error({
                            message: "Unable to access your location.",
                            description:
                                "Please ensure location permissions are enabled in your browser and try again.",
                        });
                    }}
                    onGeolocate={(e) => {
                        userLocation.current = e.coords;

                        setUserLocation(e.coords);

                        if (!showRecenterButton && userLocation.current) {
                            mapRef.current?.flyTo({
                                center: [userLocation.current.longitude, userLocation.current.latitude],
                            });
                        }
                    }}
                />
                {disableZoom && <NavigationControl />}
                {renderRecenterButton()}
            </Map>
        </MapErrorBoundary>
    );
};

export default DamnMap;
